import React, { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Search, Pencil, Trash2, ChevronUp, ChevronDown, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import TikTokPixelFormModal from '../../components/admin/TikTokPixelFormModal';

const TikTokPixelManagement: React.FC = () => {
  const [pixels, setPixels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showModal, setShowModal] = useState(false);
  const [editingPixel, setEditingPixel] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadPixels(); }, []);

  const loadPixels = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTikTokPixel();
      let data = response.data || response;
      if (data && data.results && Array.isArray(data.results)) data = data.results;
      setPixels(Array.isArray(data) ? data : data ? [data] : []);
    } catch (error) { console.error('Failed to load TikTok Pixel:', error); setPixels([]); }
    finally { setLoading(false); }
  };

  const handleEditPixel = (pixel: any) => { setEditingPixel(pixel); setShowModal(true); };

  const handleDeletePixel = async (id: number) => {
    if (!window.confirm('Delete this TikTok Pixel?')) return;
    try { await adminAPI.deleteTikTokPixel(id); await loadPixels(); }
    catch (error: any) { alert(error.response?.data?.message || 'Failed to delete'); }
  };

  const handleSubmit = async (data: any) => {
    try {
      setSubmitting(true);
      if (editingPixel) await adminAPI.updateTikTokPixel(editingPixel.id, data);
      else await adminAPI.createTikTokPixel(data);
      setShowModal(false); setEditingPixel(null); await loadPixels();
    } catch (error: any) {
      const detail = error.response?.data;
      alert(typeof detail === 'object' ? Object.entries(detail).map(([k,v]) => `${k}: ${v}`).join('\n') : 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const handleSort = (f: string) => { if (sortField === f) setSortDir(d => d==='asc'?'desc':'asc'); else { setSortField(f); setSortDir('asc'); } setCurrentPage(1); };
  const SA = ({ field }: { field: string }) => sortField !== field ? null : sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  const fmtDate = (v: string) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '�';

  const filtered = useMemo(() => {
    let list = pixels.filter(p => [p.pixel_id, p.pixel_name].some(v => v?.toLowerCase().includes(searchTerm.toLowerCase())));
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [pixels, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">TikTok Pixel</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={() => { setEditingPixel(null); setShowModal(true); }}>
            <Plus size={13} /> Add Pixel <span className="adm-btn-badge">{pixels.length}</span>
          </button>
          <button className="adm-btn adm-btn-refresh" onClick={loadPixels} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left"><span className="adm-count"><strong>{filtered.length}</strong> pixel(s)</span></div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by Pixel ID or Name�" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('pixel_id')}>Pixel ID <SA field="pixel_id" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('pixel_name')}>Name <SA field="pixel_name" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('created_at')}>Created <SA field="created_at" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('updated_at')}>Updated <SA field="updated_at" /></th>
                <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading�</td></tr>}
              {!loading && paginated.length === 0 && <tr><td colSpan={6} className="adm-empty">No TikTok Pixels configured.</td></tr>}
              {!loading && paginated.map((p: any) => (
                <tr className="adm-row" key={p.id}>
                  <td className="adm-td"><span className="adm-mono">{p.pixel_id}</span></td>
                  <td className="adm-td"><span className="adm-name-main">{p.pixel_name || '�'}</span></td>
                  <td className="adm-td"><span className={`adm-chip ${p.is_active ? 'adm-chip-green' : 'adm-chip-red'}`}>{p.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}</span></td>
                  <td className="adm-td"><span className="adm-date">{fmtDate(p.created_at)}</span></td>
                  <td className="adm-td"><span className="adm-date">{fmtDate(p.updated_at)}</span></td>
                  <td className="adm-td" style={{ padding: '6px 4px' }}>
                    <div className="adm-actions-row">
                      <button className="adm-action-btn adm-ab-edit" onClick={() => handleEditPixel(p)} title="Edit"><Pencil size={12} /></button>
                      <button className="adm-action-btn adm-ab-del" onClick={() => handleDeletePixel(p.id)} title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="adm-pagination">
          <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
          <div className="adm-page-info">{filtered.length === 0 ? '0 pixels' : `${(currentPage-1)*pageSize+1}�${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
          <div className="adm-page-controls">
            <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>�</button>
            <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>�</button>
          </div>
        </div>
      </div>
      <TikTokPixelFormModal isOpen={showModal} onClose={() => { setShowModal(false); setEditingPixel(null); }} onSubmit={handleSubmit} initialData={editingPixel} loading={submitting} />
    </div>
  );
};

export default TikTokPixelManagement;